import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { useLang } from "../lib/i18n";
import { PageHeader } from "../lib/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Mail, Siren, CalendarRange, FlaskConical, ChevronRight } from "lucide-react";

const TYPE_META = {
  signal_alert: { label: "Alerte de signal", icon: Siren, cls: "bg-red-50 text-red-700 border-red-200" },
  weekly_digest: { label: "Résumé hebdomadaire", icon: CalendarRange, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  test: { label: "E-mail de test", icon: FlaskConical, cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function AlertsHistory() {
  const { t } = useLang();
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const { data: logs, isLoading } = useQuery({
    queryKey: ["email-logs", filter],
    queryFn: async () => (await api.get("/email-logs", { params: filter === "all" ? {} : { etype: filter } })).data,
  });

  return (
    <div data-testid="alerts-history-page">
      <PageHeader title={t("Historique des Alertes")} subtitle={t("E-mails de signaux et résumés envoyés, avec date et destinataires")} />

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-5 shadow-sm flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs text-slate-500">{t("Type d'e-mail")}</Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-9 w-56" data-testid="ah-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("Tous les types")}</SelectItem>
              <SelectItem value="signal_alert">{t("Alerte de signal")}</SelectItem>
              <SelectItem value="weekly_digest">{t("Résumé hebdomadaire")}</SelectItem>
              <SelectItem value="test">{t("E-mail de test")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? <p className="text-slate-400 text-sm">{t("Chargement…")}</p> : (
        (logs || []).length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-10 text-center" data-testid="ah-empty">
            <Mail className="h-8 w-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">{t("Aucun e-mail envoyé pour le moment.")}</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>{t("Date")}</TableHead>
                  <TableHead>{t("Type")}</TableHead>
                  <TableHead>{t("Sujet")}</TableHead>
                  <TableHead className="text-right">{t("Destinataires")}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(logs || []).map((l) => {
                  const meta = TYPE_META[l.type] || { label: l.type, icon: Mail, cls: "bg-slate-100 text-slate-600 border-slate-200" };
                  const Icon = meta.icon;
                  return (
                    <TableRow key={l.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(l)} data-testid={`ah-row-${l.id}`}>
                      <TableCell className="font-mono text-xs text-slate-500">{(l.sent_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-medium border ${meta.cls}`}>
                          <Icon className="h-3 w-3" /> {t(meta.label)}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">{l.subject}</TableCell>
                      <TableCell className="text-right text-sm font-semibold">{l.recipient_count}</TableCell>
                      <TableCell className="text-right"><ChevronRight className="h-4 w-4 text-slate-300 inline" /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.subject}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">{t("Date")}</span>
                  <span className="font-mono text-xs">{(detail.sent_at || "").slice(0, 19).replace("T", " ")} UTC</span>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-400 mb-1.5">{t("Destinataires")} ({detail.recipient_count})</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(detail.recipients || []).map((r) => <span key={r} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{r}</span>)}
                  </div>
                </div>
                {detail.meta?.signals?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase text-slate-400 mb-1.5">{t("Signaux")} ({detail.meta.signal_count})</p>
                    <div className="space-y-1.5 max-h-64 overflow-y-auto">
                      {detail.meta.signals.map((s, i) => (
                        <div key={i} className="border border-slate-100 rounded-lg p-2">
                          <p className="text-xs"><b className="text-red-600">{s.type}</b> · <span className="text-slate-500">{s.zone}</span></p>
                          <p className="text-xs text-slate-600">{s.detail}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {detail.meta?.indicators && (
                  <div>
                    <p className="text-xs font-semibold uppercase text-slate-400 mb-1.5">{t("Indicateurs clés")}</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {Object.entries(detail.meta.indicators).map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between text-xs border border-slate-100 rounded px-2 py-1">
                          <span className="text-slate-500">{k}</span><b className="text-[#F05A28]">{String(v)}</b>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
